import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaBuilding } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Facility } from '../../types';

const Facilities: React.FC = () => {
	const { token } = useAuth();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [activeTab, setActiveTab] = useState<string>('All');
	const [facilities, setFacilities] = useState<Facility[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const tabs = [
		{ id: 'All', label: 'All' },
		{ id: 'Active', label: 'Active' },
		{ id: 'Inactive', label: 'Inactive' },
	];

	// Load facilities from API — this page is read-only; facilities are
	// created/edited via the Admin > Clients > Facilities section.
	const loadFacilities = useCallback(async () => {
		if (!token) return;
		try {
			setLoading(true);
			setError(null);
			const response = await apiCall('/facilities/facilities/', { method: 'GET' }, token);
			const data = await response.json();
			setFacilities(data.results || data);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to load facilities';
			setError('Failed to load facilities: ' + errorMessage);
			console.error('Error loading facilities:', err);
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadFacilities();
	}, [loadFacilities]);

	const getTypeBadge = (facility: Facility) => {
		// Handle both nested object and direct field
		const typeName = facility?.facility_type_details?.name ||
				facility?.facility_type_name ||
				'Not Set';
		const colorCode = facility?.facility_type_details?.color_code;

		if (typeName === 'Not Set') {
			return <span className="badge badge-ghost">Not Set</span>;
		}

		return (
			<span
				className="badge"
				style={{
					backgroundColor: colorCode || '#3b82f6',
					color: '#ffffff',
					border: 'none'
				}}
			>
				{typeName}
			</span>
		);
	};

	const getSortedFacilities = () => {
		let filteredFacilities = [...facilities];

		// Filter by active tab
		if (activeTab === 'Active') {
			filteredFacilities = filteredFacilities.filter(facility => facility.is_active);
		} else if (activeTab === 'Inactive') {
			filteredFacilities = filteredFacilities.filter(facility => !facility.is_active);
		}

		// Filter by search term
		if (searchTerm) {
			filteredFacilities = filteredFacilities.filter(facility =>
				facility.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				facility.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				facility.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				facility.phone?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		return filteredFacilities;
	};

	return (
		<div className="p-6">
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaBuilding className="text-primary" />
					Facilities
				</h1>
			</div>

			{error && (
				<div className="alert alert-error mb-4">
					<span>{error}</span>
				</div>
			)}

			<div className="mb-6 flex items-center space-x-4">
				<div className="join">
					<div className="relative join-item">
						<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
							<svg className="w-4 h-4 text-base-content/60" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
								<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
							</svg>
						</div>
						<input
							type="text"
							placeholder="Search facilities..."
							className="input input-bordered w-64 pl-10 join-item"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>
			</div>

			<div className="tabs tabs-bordered mb-6">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						className={`tab tab-lg ${activeTab === tab.id ? 'tab-active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
					>
						{tab.label}
						{tab.id !== 'All' && (
							<span className="ml-2 badge badge-sm">
								{tab.id === 'Active'
									? facilities.filter(f => f.is_active).length
									: facilities.filter(f => !f.is_active).length
								}
							</span>
						)}
					</button>
				))}
			</div>

			{loading ? (
				<div className="flex justify-center items-center py-12">
					<div className="loading loading-spinner loading-lg"></div>
				</div>
			) : (
				<div className="relative overflow-x-auto rounded-lg">
					<table className="table table-zebra bg-base-100/50">
						<thead>
							<tr>
								<th>Name</th>
								<th>Facility Type</th>
								<th className="text-center">Colour Code</th>
								<th>Address</th>
								<th>Status</th>
								<th>Commands</th>
							</tr>
						</thead>
						<tbody>
							{getSortedFacilities().map((facility) => (
								<tr key={facility.id}>
									<td className="font-medium">{facility.name}</td>
									<td>
										{getTypeBadge(facility)}
									</td>
									<td>
										<div className="flex justify-center items-center">
											<div
												className="w-6 h-6 rounded border border-base-300"
												style={{ backgroundColor: facility.color_code || '#808080' }}
												title={facility.color_code || 'No colour set'}
											/>
										</div>
									</td>
									<td>
										<div className="text-sm">
											{facility.street_address}
											{facility.city && facility.state && (
												<div className="text-xs opacity-60">
													{facility.city}, {facility.state} {facility.postal_code}
												</div>
											)}
										</div>
									</td>
									<td>
										<span className={`badge ${facility.is_active ? 'badge-success' : 'badge-error'}`}>
											{facility.is_active ? 'Active' : 'Inactive'}
										</span>
									</td>
									<td>
										<Link to={`/facilities/facilitydetails/${facility.id}`} className="btn btn-ghost btn-xs">
											<FaEye className="w-4 h-4" />
										</Link>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default Facilities;
